import type { ProfilePatch } from '@/lib/api'

/**
 * The profile form's rules, kept out of the component so the request body can
 * be checked without a render — it is the part of this screen that has been
 * wrong before, and wrong *silently*.
 *
 * Both workarounds this file used to carry are gone, because the gateway
 * closed both gaps (`api/internal/api/profile.go`):
 *
 * 1. **`PATCH /v1/users/me` now binds camelCase** as well as snake_case, and
 *    camelCase wins a body carrying both. It used to bind `first_name` only,
 *    so a camelCase body answered 200 having changed nothing at all — which
 *    is why the wire format is still pinned by a test below rather than left
 *    to the type checker. A 200-that-changes-nothing is invisible from here.
 * 2. **`display_name` is derived server-side** on a rename: sending
 *    `firstName`/`lastName` without `displayName` rebuilds it as "Vorname
 *    Nachname" from the resulting values (the half not sent is read off the
 *    stored row), so the rail avatar, the account menu and every sharing list
 *    follow the rename. The client no longer computes it — one policy, one
 *    implementation. `displayName` is sent only when somebody typed one, and
 *    this form has no such field.
 */
export interface ProfileDraft {
  firstName: string
  lastName: string
}

export interface ProfileErrors {
  firstName?: string
  lastName?: string
}

/** The two fields the artboard marks with a required star. */
export function validateProfile(draft: ProfileDraft): ProfileErrors {
  const errors: ProfileErrors = {}
  if (!draft.firstName.trim()) errors.firstName = 'Bitte geben Sie Ihren Vornamen an.'
  if (!draft.lastName.trim()) errors.lastName = 'Bitte geben Sie Ihren Nachnamen an.'
  return errors
}

export function hasErrors(errors: ProfileErrors): boolean {
  return Object.keys(errors).length > 0
}

/** The request body for `PATCH /v1/users/me`. See the note at the top. */
export function profilePatch(draft: ProfileDraft): ProfilePatch {
  return {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
  }
}

/** True when nothing was typed that differs from what is stored. */
export function isUnchanged(draft: ProfileDraft, stored: ProfileDraft): boolean {
  return (
    draft.firstName.trim() === stored.firstName.trim() &&
    draft.lastName.trim() === stored.lastName.trim()
  )
}
